import { Decoration, DecorationSet, EditorView, RangeSetBuilder, WidgetType } from '@uiw/react-codemirror'

export type AssetReferenceMatch = {
  from: number
  to: number
  uuid: string
}

export type AssetReferenceLabelInfo = {
  label: string | null
  typeId: string | null
}

const assetReferenceRegex = /asset:\/\/[0-9a-fA-F-]{36}/g
const assetReferenceCandidateRegex = /asset:\/\/[0-9a-fA-F-]{0,36}/g
const normalizeAssetId = (value: string) => value.trim().toLowerCase()

class AssetReferenceWidget extends WidgetType {
  private readonly label: string
  private readonly assetId: string
  private readonly from: number
  private readonly to: number

  constructor(label: string, assetId: string, from: number, to: number) {
    super()
    this.label = label
    this.assetId = assetId
    this.from = from
    this.to = to
  }

  eq(other: AssetReferenceWidget) {
    return (
      this.label === other.label && this.assetId === other.assetId && this.from === other.from && this.to === other.to
    )
  }

  ignoreEvent() {
    return false
  }

  toDOM() {
    const span = document.createElement('span')
    span.className = 'cm-asset-reference'
    span.textContent = this.label
    span.title = this.assetId
    span.dataset.assetId = this.assetId
    span.dataset.assetFrom = String(this.from)
    span.dataset.assetTo = String(this.to)
    return span
  }
}

export const findAssetReferences = (text: string): AssetReferenceMatch[] => {
  const matches: AssetReferenceMatch[] = []
  assetReferenceRegex.lastIndex = 0
  for (const match of text.matchAll(assetReferenceRegex)) {
    if (match.index === undefined) continue
    const from = match.index
    const to = from + match[0].length
    matches.push({
      from,
      to,
      uuid: match[0].slice('asset://'.length),
    })
  }
  return matches
}

export const findAssetReferenceCandidates = (text: string): AssetReferenceMatch[] => {
  const matches: AssetReferenceMatch[] = []
  assetReferenceCandidateRegex.lastIndex = 0
  for (const match of text.matchAll(assetReferenceCandidateRegex)) {
    if (match.index === undefined) continue
    const from = match.index
    const to = from + match[0].length
    matches.push({
      from,
      to,
      uuid: match[0].slice('asset://'.length),
    })
  }
  return matches
}

export const findAssetReferenceAt = (text: string, pos: number): AssetReferenceMatch | undefined =>
  findAssetReferenceCandidates(text).find((match) => pos >= match.from && pos <= match.to)

export const highlightAssetReferences = (
  view: EditorView,
  labelsById: ReadonlyMap<string, AssetReferenceLabelInfo> = new Map(),
  allowedTypeIds: ReadonlySet<string> = new Set()
): DecorationSet => {
  const builder = new RangeSetBuilder<Decoration>()
  for (const match of findAssetReferenceCandidates(view.state.doc.toString())) {
    const isComplete = match.uuid.length === 36
    if (!isComplete) {
      builder.add(match.from, match.to, Decoration.mark({ class: 'cm-asset-reference-pending' }))
      continue
    }

    const normalizedId = normalizeAssetId(match.uuid)
    const resolvedInfo = labelsById.get(normalizedId)
    const resolvedLabel = resolvedInfo?.label?.trim()
    const allowed =
      !!resolvedLabel && !!resolvedInfo?.typeId && allowedTypeIds.size > 0 && allowedTypeIds.has(resolvedInfo.typeId)
    const displayLabel = allowed ? `asset://${resolvedLabel}` : `asset://${match.uuid}`
    builder.add(
      match.from,
      match.to,
      Decoration.replace({ widget: new AssetReferenceWidget(displayLabel, match.uuid, match.from, match.to) })
    )
  }
  return builder.finish()
}
